import React, { useState, useEffect } from "react"
import { get, post, remove, put } from "../../utils/apiClient"
import Pagination from "../common/pagination"
import toast from "react-hot-toast"
import { SquarePen, Trash2 } from "lucide-react"
import PhonebookModal from "./phonebookModal"
import { formatFaxNumber } from "../../utils/common"
import Select from "react-select"
import SearchInputWrapper from "../common/searchInputWrapper"

const types = ["PERSONAL", "QUEUE"]

const Phonebook = ({ queueLevelData }) => {
  const [phonebooks, setPhonebooks] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  // const [sortConfig, setSortConfig] = useState({
  //   key: "firstName",
  //   direction: "asc",
  // })

  const [showPopup, setShowPopup] = useState(false)
  const [showDeletePopup, setShowDeletePopup] = useState(null)
  const [phonebookToEdit, setPhonebookToEdit] = useState(null)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchText, setSearchText] = useState("")
  const [selectedType, setSelectedType] = useState(null)
  const [selectedQueueNetId, setSelectedQueueNetId] = useState(null)
  const [activeRowId, setActiveRowId] = useState(null)

  useEffect(() => {
    loadPhonebooks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchPhonebooks = async () => {
    try {
      const data = await get(`/phoneBook/all`)
      setPhonebooks(data)
    } catch (error) {
      console.error("Failed to fetch phonebooks", error)
    }
  }

  const handleAddOrEditPhonebook = async (data) => {
    setActionLoading(true)
    try {
      if (phonebookToEdit) {
        let oldParams = { oldFaxNumber: data.oldFaxNumber }
        if (data.type === "QUEUE") {
          oldParams = {
            oldQueue: data.oldQueue,
            oldFaxNumber: data.oldFaxNumber,
          }
        }

        await put("/phoneBook/update", {
          firstName: data.firstName,
          lastName: data.lastName,
          emailAddress: data.emailAddress,
          company: data.company,
          faxNumber: data.faxNumber,
          queue: data.queue,
          type: data.type,
          ...oldParams,
        })
      } else {
        await post("/phoneBook/save", {
          firstName: data.firstName,
          lastName: data.lastName,
          emailAddress: data.emailAddress,
          company: data.company,
          faxNumber: data.faxNumber,
          queue: data.queue,
          type: data.type,
        })
      }

      fetchPhonebooks()
      setShowPopup(false)
      setActionLoading(false)
      setPhonebookToEdit(null)

      toast.success(
        `Phonebook has been ${
          phonebookToEdit ? "updated" : "added"
        } successfully`,
        {
          duration: 5000,
        },
      )
    } catch (error) {
      setActionLoading(false)
      toast.error(error, {
        duration: 5000,
      })
    }
  }

  const handleDeletePhonebook = async () => {
    setActionLoading(true)
    try {
      if (!showDeletePopup) return

      await remove(`/phoneBook/delete`, {
        type: showDeletePopup.type,
        queue: showDeletePopup.type === "QUEUE" ? showDeletePopup.queue : "",
        faxNumber: showDeletePopup.faxNumber,
      })

      fetchPhonebooks()
      setShowDeletePopup(null)
      setActionLoading(false)

      toast.success("Phonebook has been removed successfully", {
        duration: 5000,
      })
    } catch (error) {
      setActionLoading(false)
      toast.error(error, {
        duration: 5000,
      })
    }
  }

  const loadPhonebooks = async () => {
    setLoading(true)
    await fetchPhonebooks()
    setLoading(false)
  }

  // Search Filter
  let filteredPhonebooks = phonebooks?.filter((queue) => {
    return Object.entries(queue).some(([key, value]) => {
      // For other fields, apply standard filtering
      if (
        key === "firstName" ||
        key === "lastName" ||
        key === "faxNumber" ||
        key === "company" ||
        key === "emailAddress"
      ) {
        return value
          ?.toString()
          .toLowerCase()
          .includes(searchText.toLowerCase())
      }

      return false
    })
  })

  if (selectedType) {
    filteredPhonebooks = filteredPhonebooks.filter(
      (rec) => rec.type === selectedType.value,
    )
  }

  if (selectedQueueNetId) {
    filteredPhonebooks = filteredPhonebooks.filter(
      (rec) =>
        rec.queue === selectedQueueNetId.value ||
        rec.netId === selectedQueueNetId.value,
    )
  }

  const startIdx = (currentPage - 1) * rowsPerPage
  const currentPhonebooks = filteredPhonebooks.slice(
    startIdx,
    startIdx + rowsPerPage,
  )
  const totalPages = Math.ceil(filteredPhonebooks.length / rowsPerPage)

  const typeOptions = [...new Set(types.map((r) => r))]
    .map((q) => ({
      value: q,
      label: q,
    }))
    .sort((a, b) => a.value.localeCompare(b.value))

  const queueNetIdOptions = [
    ...new Set(phonebooks.map((r) => r.queue || r.netId)),
  ]
    .map((q) => ({
      value: q,
      label: q,
    }))
    .sort((a, b) => a.value.localeCompare(b.value))

  return (
    <div className="faxes-content">
      {loading ? (
        <div className="loading-container-fax-list">
          <div className="loading-message">Loading...</div>
        </div>
      ) : (
        <>
          <div
            className="search-container"
            style={{
              width: "100%",
              alignItems: "end",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div
              className="setting-note"
              style={{ display: "flex", gap: "5px", fontWeight: "normal" }}
            >
              <div>
                {/* <input
                  type="text"
                  placeholder={`Search...`}
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value)
                    setCurrentPage(1)
                  }}
                /> */}
                <SearchInputWrapper
                  placeholder={`Search...`}
                  value={searchText}
                  onChange={(val) => {
                    setSearchText(val)
                    setCurrentPage(1)
                  }}
                  onClear={() => {
                    setSearchText("")
                    setCurrentPage(1)
                  }}
                />
              </div>

              <Select
                className="basic-single"
                placeholder="Filter by Type"
                options={typeOptions}
                value={selectedType}
                // onChange={setSelectedType}
                onChange={(v) => {
                  setSelectedType(v)
                  setCurrentPage(1)
                }}
                isClearable
                isSearchable
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: 0,
                    minWidth: 100,
                    height: 41,
                  }),
                }}
              />

              <Select
                className="basic-single"
                placeholder="Filter by Queue/NetId"
                options={queueNetIdOptions}
                value={selectedQueueNetId}
                // onChange={setSelectedQueueNetId}
                onChange={(v) => {
                  setSelectedQueueNetId(v)
                  setCurrentPage(1)
                }}
                isClearable
                //isSearchable
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: 0,
                    minWidth: 100,
                    height: 41,
                  }),
                }}
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  marginLeft: "6px",
                }}
                onClick={() => {
                  setSearchText("")
                  setSelectedType(null)
                  setSelectedQueueNetId(null)
                }}
                title="Reset"
              >
                {" "}
                ↺
              </div>
            </div>

            <button
              className="faxes-button"
              onClick={() => {
                // setEditingPhonebook(null)
                setShowPopup(true)
              }}
              style={{ marginBottom: "5px" }}
            >
              + Add Phonebook
            </button>
          </div>
          <div className="table-container">
            <table className="faxes-table">
              <thead>
                <tr>
                  <th
                    className="faxes-th"
                    style={{ cursor: "pointer" }}
                    // onClick={() =>
                    //   setSortConfig({
                    //     key: "fax_number",
                    //     direction:
                    //       sortConfig.direction === "asc" ? "desc" : "asc",
                    //   })
                    // }
                  >
                    Fax Number
                    {/* <span>
                      {sortConfig.direction
                        ? sortConfig.key === "fax_number"
                          ? sortConfig.direction === "asc"
                            ? " 🔼"
                            : " 🔽"
                          : " ↕️"
                        : ""}
                    </span> */}
                  </th>
                  <th className="faxes-th">First Name</th>
                  <th className="faxes-th">Last Name</th>
                  <th className="faxes-th">Type</th>
                  <th className="faxes-th">Queue/NetId</th>
                  <th className="faxes-th">Email</th>
                  <th className="faxes-th">Company</th>
                  <th
                    className="faxes-th"
                    style={{ width: "100px", textAlign: "center" }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentPhonebooks.length > 0 ? (
                  currentPhonebooks?.map((phonebook) => (
                    <tr
                      key={phonebook.faxNumber}
                      className={`fax-item${activeRowId && activeRowId === phonebook.faxNumber ? " fax-action-active" : ""}`}
                      onMouseEnter={() => { if (activeRowId && activeRowId !== phonebook.faxNumber) setActiveRowId(null) }}
                    >
                      <td className="faxes-td">
                        {formatFaxNumber(phonebook.faxNumber)}
                      </td>
                      <td className="faxes-td">{phonebook.firstName}</td>
                      <td className="faxes-td">{phonebook.lastName}</td>
                      <td className="faxes-td">{phonebook.type}</td>
                      <td className="faxes-td">
                        {phonebook.type === "QUEUE"
                          ? phonebook.queue
                          : phonebook.netId}
                      </td>
                      <td className="faxes-td">{phonebook.emailAddress}</td>
                      <td className="faxes-td">{phonebook.company}</td>

                      <td className="faxes-td" style={{ textAlign: "center" }}>
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() => { setActiveRowId(phonebook.faxNumber); setPhonebookToEdit(phonebook) }}
                        >
                          <SquarePen size="18" color="black" />
                        </span>
                        <span
                          style={{ cursor: "pointer", marginLeft: "10px" }}
                          onClick={() => {
                            setActiveRowId(phonebook.faxNumber)
                            setShowDeletePopup({
                              type: phonebook.type,
                              queue: phonebook.queue,
                              faxNumber: phonebook.faxNumber,
                            })
                          }}
                        >
                          <Trash2 size="18" color="red" />
                        </span>
                      </td>

                      {/* <td className="faxes-td" style={{ width: "200px" }}>
                        <button
                          className="faxes-button"
                          onClick={() => {
                            //setEditingPhonebook(phonebook)
                            setShowPopup(true)
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="faxes-button delete-button"
                          style={{ marginLeft: "5px" }}
                          onClick={() => {
                            setShowDeletePopup({
                              type: phonebook.type,
                              queue: phonebook.queue,
                              faxNumber: phonebook.faxNumber,
                            })
                          }}
                        >
                          Delete
                        </button>
                      </td> */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="100%"
                      style={{
                        textAlign: "center",
                        padding: "10px",
                        border: "1px solid #ccc",
                      }}
                    >
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {currentPhonebooks.length > 0 && (
            <div
              className="faxes-pagination"
              style={{
                marginTop: "8px",
                display: "flex",
                justifyContent: "end",
              }}
            >
              {currentPhonebooks.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  rowsPerPage={rowsPerPage}
                  totalRows={currentPhonebooks.length}
                  onPageChange={(page) => setCurrentPage(page)}
                  onRowsPerPageChange={(count) => {
                    setRowsPerPage(count)
                    setCurrentPage(1) // Reset to first page on change
                  }}
                />
              )}
            </div>
          )}
        </>
      )}

      {(showPopup || phonebookToEdit) && (
        <PhonebookModal
          onSave={handleAddOrEditPhonebook}
          actionLoading={actionLoading}
          onClose={() => {
            setShowPopup(false)
            setPhonebookToEdit(null)
          }}
          queueLevelData={queueLevelData}
          phonebookToEdit={phonebookToEdit}
        />
      )}

      {showDeletePopup && (
        <DeleteConfirmationPopup
          onConfirm={handleDeletePhonebook}
          onCancel={() => setShowDeletePopup(null)}
          actionLoading={actionLoading}
        />
      )}
    </div>
  )
}

export default Phonebook

const DeleteConfirmationPopup = ({ onConfirm, onCancel, actionLoading }) => {
  return (
    <div className="popup-overlay">
      <div className="popup" style={{ padding: "20px" }}>
        {actionLoading ? (
          <div>In progress ...</div>
        ) : (
          <div>
            <h3>Confirm Deletion</h3>
            <p style={{ marginTop: "5px" }}>
              Are you sure you want to delete this phone book entry?
            </p>
            <div className="popup-buttons">
              <button className="cancel" onClick={onCancel}>
                Cancel
              </button>
              <button className="delete" onClick={onConfirm}>
                Delete
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
